import { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Backdrop,
  CircularProgress,
  Stack,
  Button,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  useGetDeputyDashboardSummaryV3Query,
  useGetDeputyDashboardMonthlyTrendQuery,
  useGetDistinctEndorsersQuery,
} from "../../services/report.query";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { DeputyReportPDF } from "../../Pages/Deputy Reports/summary-pdf";
import StatisticCard from "../../Pages/MainDashboard/static-card";

// ✅ Type definition for summary
type DeputySummary = {
  total: number;
  movie: number;
  tv: number;
  puv: number;
  "video-pm": number;
  "non-video-pm": number;
  others: number;
  with_violation: number;
  without_violation: number;
};

export const DeputyDashboard = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const years = Array.from({ length: currentYear - 2018 + 1 }, (_, i) => 2018 + i);

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [violationFilter, setViolationFilter] = useState("ALL");
  const [endorserFilter, setEndorserFilter] = useState("ALL");

  // Fetch endorsers for the dropdown based on selected year
  const { data: endorsersData, isLoading: isLoadingEndorsers } = useGetDistinctEndorsersQuery({
    year: selectedYear,
  });

  const endorsers = Array.isArray(endorsersData) ? endorsersData : [];

  // ✅ Fetch summary data using V3 RPC
  const {
    data: summaryData,
    isFetching: isFetchingSummary,
    isLoading: isLoadingSummary,
    isSuccess,
  } = useGetDeputyDashboardSummaryV3Query({
    year: selectedYear,
    month: selectedMonth,
    violationFilter,
    endorserFilter,
  });

  // ✅ Fetch monthly trend data
  const {
    data: trendDataRaw,
    isFetching: isFetchingTrend,
    isLoading: isLoadingTrend,
  } = useGetDeputyDashboardMonthlyTrendQuery({
    year: selectedYear,
    violationFilter,
    endorserFilter,
  });

  const summary: DeputySummary = isSuccess
    ? (summaryData as DeputySummary)
    : {
        total: 0,
        movie: 0,
        tv: 0,
        puv: 0,
        "video-pm": 0,
        "non-video-pm": 0,
        others: 0,
        with_violation: 0,
        without_violation: 0,
      };

  const PIE_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#06B6D4", "#EF4444"];

  const reportPieData = [
    { name: "Movie", value: summary.movie },
    { name: "TV", value: summary.tv },
    { name: "PUV", value: summary.puv },
    { name: "Video PMs", value: summary["video-pm"] },
    { name: "Non-Video PMs", value: summary["non-video-pm"] },
    { name: "Others", value: summary.others },
  ];

  // Process trend data: only show months up to the selected month
  const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const trendData = (trendDataRaw || [])
    .filter((d) => selectedMonth === 0 || d.month <= selectedMonth)
    .map((d) => ({
      name: MONTH_NAMES[d.month - 1],
      total: d.total,
    }));

  const statCards = [
    { label: "Total Reports", value: summary.total, redirectUrl: "/deputies/summary" },
    { label: "Movie Reports", value: summary.movie, redirectUrl: "/deputies/reviews" },
    { label: "TV Reports", value: summary.tv, redirectUrl: "/deputies/tv-reports" },
    { label: "PUV Reports", value: summary.puv, redirectUrl: "/deputies/other-reports" },
    { label: "Video PMs", value: summary["video-pm"], redirectUrl: "/deputies/other-reports" },
    { label: "Non-Video PMs", value: summary["non-video-pm"], redirectUrl: "/deputies/other-reports" },
    { label: "Others", value: summary.others, redirectUrl: "/deputies/other-reports" },
  ];

  const isLoading = isLoadingSummary || isLoadingTrend || isFetchingSummary || isFetchingTrend;

  return (
    <Box>
      <Backdrop open={isLoading} sx={{ color: "#fff", zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ pb: 5 }}>
        {/* Header & Export */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#111827">
              Deputy Reports Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overview for {selectedMonth === 0 ? "All Months" : new Date(0, selectedMonth - 1).toLocaleString("default", { month: "long" })} {selectedYear}
            </Typography>
          </Box>

          {isSuccess && (
            <PDFDownloadLink
              document={
                <DeputyReportPDF
                  summary={summary}
                  trendData={trendData}
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  violationFilter={violationFilter}
                  endorserFilter={endorserFilter}
                />
              }
              fileName={`Deputy_Dashboard_${selectedMonth}_${selectedYear}.pdf`}
              style={{
                textDecoration: "none",
              }}
            >
              {({ loading }) => (
                <Button
                  variant="contained"
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    backgroundColor: "#1D4ED8",
                    color: "white",
                    boxShadow: "0 4px 6px -1px rgba(29, 78, 216, 0.3)",
                    fontWeight: 600,
                  }}
                >
                  {loading ? "Preparing PDF..." : "Export to PDF"}
                </Button>
              )}
            </PDFDownloadLink>
          )}
        </Stack>

        {/* Info Note */}
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Note: Only <strong>Acknowledged</strong> reports are counted in these statistics.
        </Alert>

        {/* Filters */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "#fff", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select value={selectedYear} label="Year" onChange={(e) => setSelectedYear(Number(e.target.value))} sx={{ borderRadius: 2 }}>
                  {years.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select value={selectedMonth} label="Month" onChange={(e) => setSelectedMonth(Number(e.target.value))} sx={{ borderRadius: 2 }}>
                  <MenuItem value={0}>All Months</MenuItem>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <MenuItem key={month} value={month}>
                      {new Date(0, month - 1).toLocaleString("default", { month: "long" })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Violation Status</InputLabel>
                <Select value={violationFilter} label="Violation Status" onChange={(e) => setViolationFilter(e.target.value as string)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="ALL">All Reports</MenuItem>
                  <MenuItem value="WITH">With Violation</MenuItem>
                  <MenuItem value="NONE">Without Violation</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" disabled={isLoadingEndorsers}>
                <InputLabel>Endorser</InputLabel>
                <Select value={endorserFilter} label="Endorser" onChange={(e) => setEndorserFilter(e.target.value as string)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="ALL">All Endorsers</MenuItem>
                  {endorsers.map((endorser: string) => (
                    <MenuItem key={endorser} value={endorser}>{endorser}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Stat Cards matching MainDashboard */}
        <Grid container spacing={3} mb={3}>
          {statCards.map((stat, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <StatisticCard
                title={stat.label}
                total={stat.value}
                redirectUrl={stat.redirectUrl}
                percentage={0}
              />
            </Grid>
          ))}
          
          {/* 8th Card: Violation Rate Ring */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)",
                borderRadius: "12px",
                border: "none",
                height: "100%",
                display: "flex",
                alignItems: "center",
                p: 3,
              }}
            >
              <Box display="flex" alignItems="center" gap={3} width="100%">
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={70}
                    thickness={4.5}
                    sx={{ color: "#F3F4F6" }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={summary.total > 0 ? (summary.with_violation / summary.total) * 100 : 0}
                    size={70}
                    thickness={4.5}
                    sx={{
                      color: "#EF4444",
                      position: "absolute",
                      left: 0,
                      [`& .MuiCircularProgress-circle`]: {
                        strokeLinecap: "round",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2" fontWeight={800} color="#111827">
                      {summary.total > 0 ? ((summary.with_violation / summary.total) * 100).toFixed(1) : 0}%
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="#718096" fontWeight={500}>
                    With Violation
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="#1A202C">
                    {summary.with_violation}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
                    No Violation: {summary.without_violation}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)", height: 400 }}>
              <Typography variant="h6" fontWeight={700} color="#111827" mb={1}>
                Reports by Type
              </Typography>
              <Box sx={{ width: "100%", height: "100%", pb: 4 }}>
                <ResponsiveContainer>
                  <BarChart data={reportPieData} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#6b7280", fontSize: 11 }} dy={10} interval={0} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: "#6b7280", fontSize: 11 }} />
                    <Tooltip 
                      cursor={{ fill: "#f9fafb" }} 
                      contentStyle={{ 
                        borderRadius: '12px', 
                        border: "none", 
                        boxShadow: "0 10px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)", 
                        padding: '12px',
                        fontWeight: 600
                      }} 
                    />
                    <Bar dataKey="value" name="Reports" radius={[6, 6, 0, 0]} barSize={35}>
                      {reportPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)", height: 400 }}>
              <Typography variant="h6" fontWeight={700} color="#111827" mb={1}>
                Reports Trend
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {selectedMonth === 0 
                  ? `Monthly volume for ${selectedYear}` 
                  : `Monthly volume up to ${new Date(0, selectedMonth - 1).toLocaleString("default", { month: "long" })} ${selectedYear}`
                }
              </Typography>
              <Box sx={{ width: "100%", height: "80%", pb: 4 }}>
                <ResponsiveContainer>
                  <AreaChart data={trendData} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#9CA3AF", fontSize: 12 }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: "#9CA3AF", fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)" }}
                      cursor={{ stroke: '#9CA3AF', strokeWidth: 1, strokeDasharray: '3 3' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="total"
                      name="Reports"
                      stroke="#3B82F6"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorTotal)"
                      activeDot={{ r: 6, fill: "#3B82F6", stroke: "#fff", strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
